/**
 * @fileoverview Feed Polls Hook
 *
 * Fetches and transforms poll data for the voting feed.
 * Handles joining poll data with author profiles and formatting.
 *
 * @module hooks/useFeedPolls
 */

import { useQuery } from '@tanstack/react-query';
import { getAvatarUrl, getDefaultAvatarUrl } from '@/lib/avatars';
import { supabase } from '@/integrations/supabase/client';
import { FeedPostData, PollType } from '@/types/polls';
import { useAuth } from '@/hooks/useAuth';
import { PollFilter } from '@/components/polls/PollFilterChips';

/**
 * Raw poll data as stored in database.
 * @internal
 */
interface DbPoll {
  id: string;
  user_id: string;
  poll_type: string;
  caption: string | null;
  images: { id: string; url: string }[];
  votes: Record<string, number>;
  created_at: string;
  is_anonymous: boolean;
  video_url?: string | null;
  is_sponsored?: boolean;
  sponsor_name?: string;
  sponsor_cta_url?: string;
  sponsor_cta_text?: string;
  sponsor_position?: 'pinned' | 'interleaved';
  sponsor_priority?: number;
  is_official?: boolean;
}

/**
 * User profile for poll authors.
 * @internal
 */
interface Profile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

/**
 * Formats a date string into a human-readable relative time.
 */
function formatTimestamp(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Transforms a database poll record into feed display format.
 */
function transformPollToFeedPost(poll: DbPoll, profile: Profile | null): FeedPostData {
  // Pass through raw votes - FeedPost handles both legacy (yes/no) and new (imageId:up/down) formats
  const votes = (poll.votes || {}) as FeedPostData['votes'];

  const author = poll.is_anonymous
    ? {
        id: undefined,
        username: 'Anonymous',
        avatar: getDefaultAvatarUrl('anonymous'),
      }
    : {
        id: poll.user_id,
        username: profile?.display_name || 'Anonymous',
        avatar: getAvatarUrl(profile?.avatar_url, poll.user_id),
      };

  return {
    id: poll.id,
    author,
    pollType: poll.poll_type as PollType,
    caption: poll.caption || '',
    images: poll.images.map(img => ({
      id: img.id,
      url: img.url,
    })),
    videoUrl: poll.video_url || undefined,
    votes,
    timestamp: formatTimestamp(poll.created_at),
    createdAt: poll.created_at,
    isSponsored: poll.is_sponsored,
    sponsorName: poll.sponsor_name || undefined,
    sponsorCtaUrl: poll.sponsor_cta_url || undefined,
    sponsorCtaText: poll.sponsor_cta_text || undefined,
    isOfficial: poll.is_official || false,
  };
}

/**
 * Calculate total votes for a poll
 */
function getTotalVotes(votes: Record<string, number>): number {
  return Object.values(votes).reduce((sum, count) => sum + count, 0);
}

interface UseFeedPollsOptions {
  filter?: PollFilter;
}

/**
 * Hook to fetch polls for the voting feed with filtering support.
 */
export function useFeedPolls(options: UseFeedPollsOptions = {}) {
  const { filter = 'all' } = options;
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feed-polls', filter, user?.id],
    queryFn: async () => {
      // Build the base query
      let query = supabase
        .from('polls')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      // Apply filter-specific conditions
      switch (filter) {
        case 'all':
          // All public polls (no additional filtering)
          query = query.eq('visibility', 'public');
          break;

        case 'community':
          // All public polls
          query = query.eq('visibility', 'public');
          break;

        case 'following':
          // Polls from users the current user follows
          if (!user) return [];
          
          // First get the list of users being followed
          const { data: followingData } = await supabase
            .from('follows')
            .select('following_id')
            .eq('follower_id', user.id);
          
          const followingIds = followingData?.map(f => f.following_id) || [];
          if (followingIds.length === 0) return [];
          
          query = query
            .eq('visibility', 'public')
            .in('user_id', followingIds);
          break;

        case 'only-me':
          // Only the current user's polls
          if (!user) return [];
          query = query.eq('user_id', user.id);
          break;

        case 'try-ons':
          // Polls with try-on content (images from virtual-tryon)
          query = query
            .eq('visibility', 'public')
            .or('caption.ilike.%try on%,caption.ilike.%tryon%,caption.ilike.%virtual%');
          break;

        case 'videos':
          // Polls with video content
          query = query
            .eq('visibility', 'public')
            .not('video_url', 'is', null);
          break;

        case 'trending':
          // High engagement in last 24h - we'll sort by total votes
          const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
          query = query
            .eq('visibility', 'public')
            .gte('created_at', oneDayAgo);
          break;

        case 'unanswered':
          // Polls user hasn't voted on
          if (!user) return [];
          
          // Get polls user has voted on
          const { data: votedPolls } = await supabase
            .from('poll_votes')
            .select('poll_id')
            .eq('user_id', user.id);
          
          const votedPollIds = votedPolls?.map(v => v.poll_id) || [];
          
          query = query.eq('visibility', 'public');
          
          // Exclude polls the user has voted on
          if (votedPollIds.length > 0) {
            // Use not.in to exclude voted polls
            query = query.not('id', 'in', `(${votedPollIds.join(',')})`);
          }
          break;
      }

      const { data: polls, error: pollsError } = await query;

      if (pollsError) throw pollsError;
      if (!polls || polls.length === 0) return [];

      // Get unique non-anonymous user IDs
      const userIds = [...new Set(
        polls
          .filter(p => !p.is_anonymous)
          .map(p => p.user_id)
      )];

      // Fetch profiles
      const profileMap = new Map<string, Profile>();
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name, avatar_url')
          .in('user_id', userIds);
        
        (profiles || []).forEach(p => {
          profileMap.set(p.user_id, p);
        });
      }

      // Transform polls
      const feedPosts: FeedPostData[] = [];
      const pollData: { isSponsored: boolean; sponsorPosition: string | null; totalVotes: number }[] = [];
      
      for (const poll of polls) {
        const parsedPoll: DbPoll = {
          ...poll,
          images: Array.isArray(poll.images) ? poll.images as { id: string; url: string }[] : [],
          votes: typeof poll.votes === 'object' && poll.votes !== null ? poll.votes as Record<string, number> : {},
          is_anonymous: poll.is_anonymous ?? false,
          video_url: poll.video_url ?? null,
          is_sponsored: poll.is_sponsored ?? false,
          sponsor_name: poll.sponsor_name ?? undefined,
          sponsor_cta_url: poll.sponsor_cta_url ?? undefined,
          sponsor_cta_text: poll.sponsor_cta_text ?? undefined,
          sponsor_position: poll.sponsor_position as 'pinned' | 'interleaved' | undefined,
          sponsor_priority: poll.sponsor_priority ?? 0,
          is_official: poll.is_official ?? false,
        };
        
        const profile = poll.is_anonymous ? null : profileMap.get(poll.user_id) || null;
        feedPosts.push(transformPollToFeedPost(parsedPoll, profile));
        pollData.push({ 
          isSponsored: poll.is_sponsored ?? false, 
          sponsorPosition: poll.sponsor_position,
          totalVotes: getTotalVotes(parsedPoll.votes),
        });
      }

      // For trending, sort by total votes
      if (filter === 'trending') {
        const indexed = feedPosts.map((post, i) => ({ post, votes: pollData[i].totalVotes }));
        indexed.sort((a, b) => b.votes - a.votes);
        return indexed.map(item => item.post);
      }

      // For community filter, handle sponsored content interleaving
      if (filter === 'community') {
        const sponsoredPinned: FeedPostData[] = [];
        const sponsoredInterleaved: FeedPostData[] = [];
        const officialPolls: FeedPostData[] = [];
        const regularPolls: FeedPostData[] = [];
        
        feedPosts.forEach((post, i) => {
          const data = pollData[i];
          if (post.isOfficial) {
            officialPolls.push(post);
          } else if (data.isSponsored && data.sponsorPosition === 'pinned') {
            sponsoredPinned.push(post);
          } else if (data.isSponsored && data.sponsorPosition === 'interleaved') {
            sponsoredInterleaved.push(post);
          } else if (!data.isSponsored) {
            regularPolls.push(post);
          }
        });

        const result: FeedPostData[] = [...sponsoredPinned, ...officialPolls];
        let sponsoredIdx = 0;
        
        regularPolls.forEach((post, i) => {
          result.push(post);
          if ((i + 1) % 5 === 0 && sponsoredIdx < sponsoredInterleaved.length) {
            result.push(sponsoredInterleaved[sponsoredIdx]);
            sponsoredIdx++;
          }
        });

        while (sponsoredIdx < sponsoredInterleaved.length) {
          result.push(sponsoredInterleaved[sponsoredIdx]);
          sponsoredIdx++;
        }

        return result;
      }
      // Pin official roadmap polls near the top
      const officialPolls = feedPosts.filter(p => p.isOfficial);
      if (officialPolls.length > 0) {
        const rest = feedPosts.filter(p => !p.isOfficial);
        return [...officialPolls, ...rest];
      }

      return feedPosts;
    },
  });
}
